import React from "react";

import { useMutation } from "@apollo/client";
import {
  Avatar,
  Button,
  ButtonBase,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useBooleanToggle from "@/hooks/useBooleanToggle";
import useDialogState from "@/hooks/useDialogState";
import useFragment from "@/hooks/useFragment";
import { format, formatISODateTime } from "@/utils/datetime";
import { apply } from "@/utils/functions";

import MealCommentField from "./MealCommentField";

interface Props {
  coachId?: string;
  id: string;
}

export const mealCommentFragment = graphql(`
  fragment ClientMealComment on MealComment {
    id
    mealId
    author {
      id
      profile {
        id
        fullName
        avatarUrl(variant: THUMB)
      }
    }
    body
    createdAt
    deletedAt
  }
`);

const updateMealCommentMutation = graphql(`
  mutation UpdateMealComment($input: UpdateMealCommentInput!) {
    updateMealComment(input: $input) {
      mealComment {
        id
        body
      }
    }
  }
`);

const deleteMealCommentMutation = graphql(`
  mutation DeleteMealComment($input: DeleteMealCommentInput!) {
    deleteMealComment(input: $input) {
      mealComment {
        id
        body
        deletedAt
      }
    }
  }
`);

const MealComment: React.FC<Props> = ({ id, coachId }) => {
  const { classes, cx } = useStyles();

  const [comment] = useFragment(mealCommentFragment, `MealComment:${id}`);

  const [isEditing, setIsEditing] = useBooleanToggle();
  const deleteDialog = useDialogState();

  const [updateMealComment] = useMutation(updateMealCommentMutation);
  const updateComment = async (body: string) => {
    setIsEditing.setFalse();
    await updateMealComment({
      variables: { input: { commentId: id, body } },
      optimisticResponse: {
        updateMealComment: {
          mealComment: {
            __typename: "MealComment",
            id,
            body,
          },
        },
      },
    });
  };

  const [deleteMealComment] = useMutation(deleteMealCommentMutation);
  const deleteComment = async () => {
    setIsEditing.setFalse();
    deleteDialog.hide();
    await deleteMealComment({
      variables: { input: { commentId: id } },
      optimisticResponse: {
        deleteMealComment: {
          mealComment: {
            __typename: "MealComment",
            id,
            body: "This comment was deleted.",
            deletedAt: formatISODateTime(new Date()),
          },
        },
      },
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Avatar src={comment?.author.profile?.avatarUrl ?? undefined} alt="" />
        <div>
          <Typography className={classes.authorName}>
            {comment?.author.profile?.fullName}
          </Typography>
          <Typography className={classes.createdAt}>
            {apply(comment?.createdAt, (datetime) =>
              format({ datetime }, "P p")
            )}
          </Typography>
        </div>
      </div>
      {isEditing ? (
        <ClickAwayListener onClickAway={setIsEditing.setFalse}>
          <div>
            <MealCommentField
              value={comment?.body}
              autoFocus
              onSubmit={updateComment}
              onDelete={deleteDialog.show}
            />
          </div>
        </ClickAwayListener>
      ) : (
        <ButtonBase
          className={classes.editButton}
          onClick={setIsEditing.setTrue}
          disabled={comment?.author.id !== coachId || !!comment?.deletedAt}
        >
          <Typography
            className={cx(
              classes.body,
              !!comment?.deletedAt && classes.bodyDeleted
            )}
          >
            {comment?.body}
          </Typography>
        </ButtonBase>
      )}
      <Dialog
        {...deleteDialog.props}
        aria-labelledby="confirm-delete-dialog-title"
        aria-describedby="confirm-delete-dialog-description"
      >
        <DialogTitle id="confirm-delete-dialog-title">
          Delete comment?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-delete-dialog-description">
            This action is irreversible. Do you wish to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={deleteDialog.hide} color="primary" variant="text">
            CANCEL
          </Button>
          <Button
            onClick={deleteComment}
            color="primary"
            variant="text"
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const useStyles = makeStyles()({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "16px 0",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  authorName: {
    color: "#363E45",
    fontSize: 16,
  },
  createdAt: {
    marginTop: 4,
    color: "#4C515A",
    fontSize: 10,
    fontWeight: 700,
  },
  editButton: {
    justifyContent: "start",
    textAlign: "start",
  },
  body: {
    color: "#4C515A",
    fontSize: 14,
    fontWeight: 600,
  },
  bodyDeleted: {
    fontStyle: "italic",
    opacity: 0.5,
  },
});

export default MealComment;
